/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, useStyleConfig} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {SwatchCheckIcon} from '../icons'
import {useGetContentByTitle} from '../../custom-api/hooks/use-get-content-by-title'

/**
 * AlternativeSwatchGroup displays alternative product variants (e.g., weight, size)
 * as tabs instead of traditional swatches, without price information.
 */
const AlternativeSwatchGroup = ({label, c_alternativeSwatch, options = [], value, onTabChange}) => {
    const styles = useStyleConfig('AlternativeSwatchGroup')
    const {buildLocalizedUrl} = useMulticountryUtils()
    const {data: techSpecLabels} = useGetContentByTitle('Tech specs labels')

    if (!options || options.length === 0) {
        return null
    }
    //option starts with a number we sort lower to higher, else as we receive them
    const sortedOptions = /^\d/.test(String(options[0]?.name ?? '').trim())
        ? options
              .map((option) => ({...option}))
              .sort((a, b) => {
                  return a.name > b.name ? -1 : a.name < b.name ? 1 : 0
              })
        : options
    // Get label from tech_spec using c_alternativeSwatch key
    const techSpecKey = c_alternativeSwatch ? `tech_spec_${c_alternativeSwatch}` : null
    const displayLabel =
        techSpecKey && techSpecLabels?.[techSpecKey]?.defaultMessage
            ? techSpecLabels[techSpecKey].defaultMessage
            : label

    return (
        <Box data-ab="variant-alternatives">
            <Box {...styles?.groupLabel}>
                <Box {...styles?.groupLabelText}>{displayLabel}</Box>
            </Box>
            <Flex {...styles?.tabsWrapper} role="radiogroup" aria-label={label}>
                {sortedOptions.map((option) => {
                    const {href, name, value: optionValue} = option
                    const isSelected = optionValue === value

                    return (
                        <Button
                            key={optionValue}
                            as={href ? RouteLink : 'button'}
                            to={href ? buildLocalizedUrl(href) : undefined}
                            {...styles?.tab}
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() => !href && onTabChange?.(optionValue)}
                            {...(isSelected ? styles?.tabSelected : styles?.tabDefault)}
                        >
                            <Box as="span" {...styles?.tabContent}>
                                <Box as="span">{name}</Box>
                                <Box
                                    as="span"
                                    {...(isSelected
                                        ? styles?.radioIndicatorSelected
                                        : styles?.radioIndicator)}
                                >
                                    {isSelected && (
                                        <SwatchCheckIcon
                                            {...styles?.radioCheckIcon}
                                            aria-hidden="true"
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Button>
                    )
                })}
            </Flex>
        </Box>
    )
}

AlternativeSwatchGroup.displayName = 'AlternativeSwatchGroup'

AlternativeSwatchGroup.propTypes = {
    /**
     * The label for the alternative swatch group (e.g., "Weight", "Size")
     */
    label: PropTypes.string,
    /**
     * The c_alternativeSwatch value used to look up tech_spec translation
     */
    c_alternativeSwatch: PropTypes.string,
    /**
     * Array of alternative swatch options with href, name, and value
     */
    options: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            value: PropTypes.string,
        }),
    ),
    /**
     * Currently selected value
     */
    value: PropTypes.string,
    /**
     * Callback when a tab is selected
     */
    onTabChange: PropTypes.func,
}

export default AlternativeSwatchGroup
